<script setup lang="ts">
import LoadingSpin from "@/components/icons/LoadingSpin.vue";
import { computed, onMounted } from "vue";
interface Option {
  label: string;
  value?: any;
  error?: string;
  /** Opción visible pero no seleccionable (p. ej. horas ya pasadas). */
  disabled?: boolean;
}

interface Props {
  modelValue: any;
  options: Option[];
  label?: string;
  placeholder?: string;
  selectTooltip?: string;
  defaultValue?: any;
  disabled?: boolean;
  isRequired?: boolean;
  name?: string;
  error?: string;
  onEmits?: (value: any) => void;
  classLabel?: string;
  loadingOptions?: boolean;
}

interface Emits {
  (event: "update:modelValue", value: any): void;
  (event: "focusIn", value: FocusEvent): void;
  (event: "focusOut", value: FocusEvent): void;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "Seleccione una opción",
  defaultValue: "",
  classLabel: "font-medium text-base",
  loadingOptions: false,
});

const emits = defineEmits<Emits>();

const parseValue = (value: string) => {
  return value.match(/^\d+$/g) ? JSON.parse(value) : value;
};

const value = computed(() => {
  // Manejar explícitamente valores null y undefined
  if (props.modelValue === null || props.modelValue === undefined) {
    return "";
  }
  // Si es un número o string válido, convertir a string
  const stringValue = props.modelValue.toString();
  return stringValue === "null" ? "" : stringValue;
});

const disabledDefaultValue = "";

const handleEmit = (value: string) => {
  if (value === "" || value === "null") {
    // Convertir tanto cadenas vacías como "null" a null real
    props.onEmits
      ? props.onEmits(null)
      : emits("update:modelValue", null);
  } else {
    props.onEmits
      ? props.onEmits(parseValue(value))
      : emits("update:modelValue", parseValue(value));
  }
};

onMounted(() => {
  if (props.modelValue) {
    emits("update:modelValue", props.modelValue);
  }
});
</script>

<template>
  <label class="flex flex-col gap-1">
    <div class="flex items-center justify-between">
      <span v-if="label" :class="classLabel" class="">{{ label }} <span v-if="isRequired" class="text-red-500">*</span>
      </span>
      <LoadingSpin v-if="loadingOptions" class="!h-4 !w-4" dark />
    </div>

    <select :value="value" v-tooltip="selectTooltip" :disabled="disabled" :name="name"
      @focusin="(e) => emits('focusIn', e)" @focusout="(e) => emits('focusOut', e)"
      @input="(e) => handleEmit((e.target as HTMLInputElement).value)" :class="[
        'py-2.5 px-2  rounded-md w-full border-2 text-sm border-gray-300 focus:ring-gray-200',
        disabled
          ? 'cursor-not-allowed bg-slate-200'
          : 'cursor-pointer bg-white',
      ]">
      <option v-if="!value || value === null || value === undefined || value === ''" value="" disabled selected hidden class="!text-gray-400">
        {{ placeholder }}
      </option>

      <option v-for="(option, index) in options" :key="index" :value="option.value" :disabled="option.disabled">
        {{ option.label }}
      </option>

      <option v-if="options.length == 0" disabled>
        No hay opciones disponibles
      </option>
    </select>
    <span v-if="error" class="!text-sm text-red-500">{{ error }}</span>
  </label>
</template>

<style scoped></style>
